import React from 'react';
import { shallowEqual } from 'react-redux';
import { Box } from '@apia/theme';
import focus from '../../../../../classes/focusController';
import { useAppSelector } from '../../../../../hooks/hooks';
import store from '../../../../../store';
import propsStore from '../../../../../store/propsStore/propsStore';
import usePropsSelector from '../../../../../store/propsStore/usePropsSelector';
import { TFileUploaded } from '../../../../../types';
import { noNaN } from '../../../../../utils/util';
import Uploader from '../../../../common/Uploader';
import DefaultUploadView from '../../../../common/uploader/DefaultUploadView';
import { dropzoneEmitter } from '../../../../common/uploader/DropZone';
import {
  TDocType,
  TFilesListChange,
  TUploaderFileInfo,
} from '../../../../common/uploader/types';
import {
  parseFileDefinition,
  uploaders,
} from '../../../../common/uploader/UploaderApi';
import { makeDocTypeDescription } from '../../../common/modals/documentUploadModal/makeDocTypeTooltip';
import { useTranslateId } from '../../../common/modals/translateModal/TranlateContext';
import { useApiaComponentContext } from '../../apiaComponent/ApiaComponentContext';
import { useFieldContext } from '../../fieldsStore/FieldContext';
import MakeField from '../../fieldsStore/MakeField';
import { useApiaFormContext } from '../../page/ApiaForm';
import { getFieldErrorStyles } from '../../style';
import useApiaFieldCommonProps from '../useApiaFieldCommonProps';
import makeUploaderApi from './ApiaUploaderApi';
import { getFilePropValue } from './util';
import IProperty from '../../../../../JSApi/constants/IProperty';

const ApiaUploader = MakeField(
  React.forwardRef(() => {
    const {
      setValue,
      state: { isValid, value },
      doFocus,
    } = useFieldContext();

    const [ref, setRef] = React.useState<HTMLDivElement | null>(null);

    const {
      isReadonly,
      identifier: { 'data-name': dataName, ...identifier },
    } = useApiaFieldCommonProps({ className: 'uploader' });
    React.useEffect(
      () =>
        doFocus(() => {
          void focus.afterNotifications(
            () => {
              const withMenuElement = ref?.querySelector(
                '.uploaderFilesList__withMenuElement__optionsButton:last-of-type',
              );
              return (withMenuElement ||
                ref?.querySelector('.dropzone')) as HTMLElement;
            },
            {
              scrollIntoViewOptions: {
                behavior: 'auto',
                block: 'center',
                inline: 'center',
              },
            },
          );
        }),
      [doFocus, ref],
    );

    const { fieldId } = useApiaComponentContext();

    const { file_prp: fileDefinition, properties } = usePropsSelector(
      fieldId,
      (current) => current.field,
    );

    const { isFormReadOnly } = useApiaFormContext();

    const {
      hideSignButtons,
      isRequired,
      frmParent,
      langs,
      noModify,
      noEdit,
      noDownload,
      noErase,
      noLock,
      disabled: isDisabled,
      noSign,
      showDescription,
      ariaLabel,
      ariaDescription,
      title,
      autoFocus,
    } = usePropsSelector(
      fieldId,
      (props) => {
        return {
          disabled: props.disabled || props[IProperty.PROPERTY_INPUT_AS_TEXT],
          frmParent: props.formData?.frmParent,
          isRequired: props.required,
          langs: propsStore.getFieldProps(props.formData.id).formData.langs,
          noDownload: props.noDownload,
          noEdit: props.allowEdition === false || isFormReadOnly,
          noErase: props.noErase || isFormReadOnly,
          noLock: props.noLock || isFormReadOnly,
          noModify: props.noModify || isFormReadOnly,
          noSign: props.hideSignButtons || isFormReadOnly,
          showDescription: props.fileShowDesc === false,
          hideSignButtons: props.hideSignButtons,
          ariaLabel: props[IProperty.PROPERTY_ARIA_LABEL],
          ariaDescription: props[IProperty.PROPERTY_ARIA_DESCRIPTION],
          title: props[IProperty.PROPERTY_TITLE],
          autoFocus:
            props[IProperty.PROPERTY_AUTO_FOCUS] === 'true' ? true : undefined,
        };
      },
      shallowEqual,
    );

    const translationId = useTranslateId();
    const Api = React.useMemo(() => {
      return makeUploaderApi({
        langs: frmParent === 'E' ? langs : undefined,
        fieldId,
        initialFiles: getFilePropValue(fileDefinition),
        fieldProps: properties,
        translationId,
      });
    }, [fieldId, fileDefinition, frmParent, langs, properties, translationId]);

    const variantMemo = React.useMemo(
      () => ({ variant: 'layout.execution.form.fields.uploader.defaultView' }),
      [],
    );

    const [actualValue, setActualValue] = React.useState<TUploaderFileInfo[]>(
      Array.isArray(value) ? value : [],
    );
    React.useEffect(() => {
      const api = uploaders[fieldId];
      if (Array.isArray(value))
        if (value.length > 0)
          setActualValue([api.parseFileDefinition(value[0] as TFileUploaded)]);
        else setActualValue([]);
      else if (value === '') setActualValue([]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleChange = React.useCallback<TFilesListChange>(
      (ev) => {
        setValue(ev.length);
        propsStore.updateField(fieldId, { value: ev });
      },
      [fieldId, setValue],
    );

    const currentDocType = useAppSelector((global) => {
      const allowedTypes =
        global.fileUploadSlice.uploaders[fieldId]?.allowedTypes;
      if (allowedTypes?.length === 1) return allowedTypes[0];

      const generic = allowedTypes?.find(
        (search) => search.id === '1',
      ) as TDocType;
      const maxSize =
        allowedTypes?.reduce((max, current) => {
          return noNaN(current.maxSize, 0) > noNaN(max)
            ? noNaN(current.maxSize, 0)
            : noNaN(max);
        }, 0) ?? 0;
      return {
        ...generic,
        maxSize,
      };
    }, shallowEqual);

    const description = React.useMemo(
      () => makeDocTypeDescription(currentDocType),
      [currentDocType],
    );

    const file = Object.values(
      store.getState().fileUploadSlice.uploaders[fieldId]?.files ?? {},
    )[0];
    const docDescription = file?.docDescription || file?.description;

    const sx = React.useMemo(() => getFieldErrorStyles(isValid), [isValid]);

    const onOneClickUpload = React.useCallback(() => {
      dropzoneEmitter.emit('open', {
        id: fieldId,
        fileName: file?.docName || file?.name,
        fromIcon: true,
      });
    }, [fieldId, file?.docName, file?.name]);

    return (
      <Box
        {...identifier}
        id={undefined}
        data-name={dataName}
        data-required={isRequired}
        ref={setRef}
        sx={sx}
      >
        <Uploader
          allowDrop={properties.fileNoAllwDnD !== false}
          filesList={actualValue}
          noModify={noModify || isReadonly || isDisabled}
          maxFiles={1}
          onFilesListChange={handleChange}
          type={frmParent as 'E' | 'P'}
          Api={Api}
          id={identifier.id}
          isReadonly={isReadonly || isDisabled}
          dropzoneId={fieldId}
          ariaLabel={ariaLabel || description}
          ariaDescription={ariaDescription}
          title={title}
          autoFocus={autoFocus}
        >
          <DefaultUploadView
            autoFocus={autoFocus}
            data-variant="layout.execution.form.fields.uploader.defaultView"
            sx={variantMemo}
            {...{
              noModify,
              noDownload,
              noErase,
              noLock,
              noSign,
              isReadonly,
              isDisabled,
              noEdit,
              onOneClickUpload,
              noVerifySign: hideSignButtons,
            }}
          />
        </Uploader>
        {showDescription && docDescription}
      </Box>
    );
  }),
  {
    getInitialValue({
      context: {
        field: { file_prp },
      },
    }) {
      const nonParsedFile = Object.values(getFilePropValue(file_prp))[0];
      if (nonParsedFile) {
        return [parseFileDefinition(nonParsedFile, false)];
      }
      return [];
    },
    preSynchronize: () => true,
    doSynchronize: () => true,
  },
);

export default ApiaUploader;
